/** App Store id for "Opendoor: Buy & Sell Homes" (`com.opendoor.buyerapp`). */
export const IOS_APP_STORE_ID = '1143708068';

/**
 * Query parameter the iOS app reads as "this link is meant to stay in the
 * browser". `DeepLinkHandler.parseListingURL` returns no route when it is
 * present, which hands control straight back to Safari, so any URL we hand to
 * the app for the open-in-app path must not carry it.
 */
const DEEP_LINK_OPT_OUT_PARAM = 'source';
const DEEP_LINK_OPT_OUT_VALUE = 'web';

/**
 * Strips the browser-only marker so a listing URL assembled for the web can be
 * handed to the app without silently bouncing back out of it.
 */
export const toAppDeepLinkUrl = (listingUrl: string): string => {
  const url = new URL(listingUrl);
  const survivors = url.searchParams
    .getAll(DEEP_LINK_OPT_OUT_PARAM)
    .filter((value) => value !== DEEP_LINK_OPT_OUT_VALUE);
  url.searchParams.delete(DEEP_LINK_OPT_OUT_PARAM);
  for (const value of survivors) {
    url.searchParams.append(DEEP_LINK_OPT_OUT_PARAM, value);
  }
  return url.toString();
};

type BuildSmartAppBannerContentOptions = {
  /**
   * Canonical listing URL passed to the app as `app-argument`. Safari hands it
   * to the app verbatim when the banner's "OPEN" action is used, so it must be
   * a `/properties/{slug}/aid_{addressId}` URL the app can route.
   */
  listingUrl: string;
};

/**
 * Builds the `apple-itunes-app` meta content for Safari's smart app banner.
 *
 * Safari resolves the install state itself: "OPEN" deep links into the app via
 * `app-argument`, "VIEW" sends the visitor to the App Store. That split is the
 * only same-domain open-in-app path on iOS — Safari suppresses universal links
 * whose domain matches the page's own domain.
 */
export const buildSmartAppBannerContent = ({
  listingUrl,
}: BuildSmartAppBannerContentOptions): string =>
  `app-id=${IOS_APP_STORE_ID}, app-argument=${toAppDeepLinkUrl(listingUrl)}`;
